/**
 * Andamento dei servizi ricorrenti e una tantum negli ultimi mesi (regole pure, testate in
 * __tests__/serviceTrends.test.ts). Prima il codice cercava service_type 'recurring' / 'subscription', che
 * nel database non esistono (i valori sono one_time | monthly | annual), quindi il ricorrente restava sempre 0.
 */

/** Nomi brevi dei mesi in italiano (indice 0 = gennaio). */
export const MONTH_SHORT_IT = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];

/** Etichetta "gen 26" (mese a base 0, anno a quattro cifre). */
export const monthLabelIt = (monthIndex: number, year: number): string =>
  `${MONTH_SHORT_IT[monthIndex]} ${String(year).slice(2)}`;

/** Chiave del mese AAAA-MM calcolata sull'ora locale (toISOString() la sposterebbe in UTC). */
export const monthKeyLocal = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

/** Ricorrente = mensile o annuale. */
export const isRecurringType = (serviceType: string | null | undefined): boolean =>
  serviceType === 'monthly' || serviceType === 'annual';

/** Entrata ricorrente di un mese: il valore del mese per il mensile, un dodicesimo per l'annuale. */
export function recurringMonthlyValue(serviceType: string, amount: number, discountedAmount: number | null | undefined): number {
  const unit = Number(discountedAmount ?? amount) || 0;
  return serviceType === 'annual' ? unit / 12 : unit;
}

export interface TrendLine {
  signedAt: string;
  serviceType?: string | null;
  amount: number;
  discountedAmount?: number | null;
  monthlyDuration?: number | null;
}

export interface TrendMonth {
  month: string;
  monthNumber: number;
  year: number;
  oneTimeRevenue: number;
  recurringRevenue: number;
  totalRevenue: number;
  mrr: number;
}

/** Finestra degli ultimi `months` mesi: da inizio del primo a inizio del mese dopo l'ultimo (istanti completi). */
export function trendWindow(now: Date, months: number): { startDate: Date; endDate: Date } {
  return {
    startDate: new Date(now.getFullYear(), now.getMonth() - months + 1, 1),
    endDate: new Date(now.getFullYear(), now.getMonth() + 1, 1),
  };
}

/** Somma le righe servizio per mese; tutti i mesi della finestra sono presenti, anche a zero. */
export function aggregateServiceTrends(lines: TrendLine[], now: Date, months: number): TrendMonth[] {
  const byKey = new Map<string, TrendMonth>();
  const ordered: TrendMonth[] = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const row: TrendMonth = {
      month: monthLabelIt(d.getMonth(), d.getFullYear()),
      monthNumber: d.getMonth() + 1,
      year: d.getFullYear(),
      oneTimeRevenue: 0,
      recurringRevenue: 0,
      totalRevenue: 0,
      mrr: 0,
    };
    byKey.set(monthKeyLocal(d), row);
    ordered.push(row);
  }

  for (const line of lines) {
    const row = byKey.get(monthKeyLocal(new Date(line.signedAt)));
    if (!row) continue;
    const unit = Number(line.discountedAmount ?? line.amount) || 0;
    const revenue = unit * (line.monthlyDuration || 1);
    const type = line.serviceType || 'one_time';
    if (isRecurringType(type)) {
      row.recurringRevenue += revenue;
      row.mrr += recurringMonthlyValue(type, Number(line.amount) || 0, line.discountedAmount);
    } else {
      row.oneTimeRevenue += revenue;
    }
    row.totalRevenue += revenue;
  }
  return ordered;
}
